import React, { useEffect, useState, useMemo } from 'react';
import { Button, Card } from 'antd';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';

import Header from 'components/Header';
import Icon from 'components/Icon';
import Loader from 'components/Loader';
import Notebook from 'components/Notebook';
import CategoryToggle from './components/CategoryToggle';
import { NotebookFilters } from './NotebookFilters';

import {
  NotebookFilter,
  useNetworks,
  ProductType
} from 'apiTyped/hooks/notebooks';
import { useNotebooks } from 'apiTyped/hooks/notebooks';
import { useUserMe } from 'apiTyped/hooks/users.query';
import { isSpiritOnly, showNCBars } from 'utils/personalisation';

const isMobile = window.innerWidth < 845;

const Container = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
  overflow: hidden;
  padding: 20px;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    max-width: 100%;
    padding: 16px;
  }

  .filters,
  .navigation {
    margin-top: 20px;
  }

  .notebook-container {
    display: flex;
  }
`;

const NotebookGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  width: 100%;
  gap: 20px;
`;

const ActionButtonContainer = styled(Button).attrs(() => ({
  type: 'primary',
  shape: 'round',
  target: '_blank'
}))`
  background-color: ${(props) => props.theme.colors['terra-cotta']};
  color: ${(props) => props.theme.colors['white']};
  font-family: ${(props) => props.theme.fonts.oswald};
  border: none;
  padding: 3px;
  height: auto;
  max-width: 300px;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    max-width: none;
    width: 100%;
    height: 40px;
    padding: 12px;
    font-size: 12px;
    letter-spacing: 2px;
  }

  &.ant-btn-primary:not(:disabled):not(.ant-btn-disabled):hover {
    background-color: ${(props) => props.theme.colors['terra-cotta']};
    color: ${(props) => props.theme.colors['white']};
    border: none;
  }
`;

export const MarketNotebooks = () => {
  const { user } = useUserMe();
  const clientTags = useMemo(() => user.client.tags || [], [user]);
  const isSpiritOnlyFlag = isSpiritOnly(clientTags);
  const hasSpirit = showNCBars(clientTags);
  const shouldShowCategoryToggle = hasSpirit && !isSpiritOnlyFlag;

  const [filterValue, setFilterValue] = useState<NotebookFilter>({
    network: undefined,
    country: undefined,
    market: undefined,
    year: undefined,
    subscribed: false,
    productType: isSpiritOnlyFlag ? ProductType.Spirit : ProductType.Wine
  });

  const { notebooksResponse, isLoadingNotebooks } = useNotebooks(filterValue);
  const { networksResponse } = useNetworks();

  const sortedNotebooks = notebooksResponse?.sort((a, b) => {
    if (a.continuous_mode && !b.continuous_mode) {
      return -1;
    } else if (!a.continuous_mode && b.continuous_mode) {
      return 1;
    }
    return 0;
  });

  useEffect(() => {
    if (networksResponse && filterValue.network === undefined) {
      const defaultNetwork = networksResponse.find(
        (network) => network.code === 'ON'
      )!;

      setFilterValue((prev) => ({
        ...prev,
        network: {
          label: I18n.t(
            `notebooks.filters.network.items.${defaultNetwork.code}`
          ),
          value: defaultNetwork.id
        }
      }));
    }
  }, [networksResponse]);

  return (
    <Container>
      <div className="notebook-container">
        <Header
          title={I18n.t('notebooks.title')}
          subTitle={I18n.t('notebooks.subTitle')}
          content={I18n.t('notebooks.content')}
          mainActionComponent={
            shouldShowCategoryToggle && (
              <CategoryToggle
                value={filterValue.productType}
                onChange={(productType) => {
                  setFilterValue({ ...filterValue, productType: productType });
                }}
              />
            )
          }
        >
          <ActionButtonContainer
            href={
              'https://wineservices-production.s3.amazonaws.com/static/WS_Markets.pdf'
            }
            icon={!isMobile && <Icon icon="download" alt="download-icon" />}
          >
            {I18n.t('dashboard.calendarNextSurveysTitle').toUpperCase()}
          </ActionButtonContainer>
        </Header>
      </div>
      <NotebookFilters
        filterValue={filterValue}
        onFilterChange={(value) => {
          setFilterValue(value);
        }}
      />
      <NotebookGrid>
        {sortedNotebooks?.length === 0 && (
          <Card
            style={{
              gridColumn: 'span 4',
              textAlign: 'center',
              padding: 20,
              borderRadius: 10
            }}
          >
            {I18n.t('notebooks.noNotebooks')}
          </Card>
        )}
        {isLoadingNotebooks && (
          <Card
            style={{
              width: '100%',
              textAlign: 'center',
              padding: 20,
              borderRadius: 10
            }}
          >
            <Loader />
          </Card>
        )}
        {notebooksResponse?.map((notebook, index) => (
          <div className="notebook-item" key={index}>
            <Notebook
              key={index}
              name={notebook.market_name}
              networkCode={notebook.network_code}
              releaseDate={notebook.release_date}
              dnClient={notebook.dn_client}
              imagePath={notebook.market_image_path}
              presentationPath={notebook.market_presentation_path}
              subscribed={notebook.subscribed}
              continuousMode={notebook.continuous_mode}
              fullContent={true}
            />
          </div>
        ))}
      </NotebookGrid>
    </Container>
  );
};
